import * as React from 'react';
import _ from 'lodash';
import {
	Table,
	TableBody,
	TableHead,
	LoadingIndicator,
	Paragraph,
	Card,
	Svg,
	TextLink,
	TertiaryButton,
} from '@mlc/web-ui-toolkit';
import { plus, exclamationTriangle } from '@mlc/symbols';
import * as palette from '@mlc/mlc-palette';
import searchTestIds from '../SearchResults.elements';
import NavigateToTopButton from '../../NavigateToTopButton/NavigateToTopButton';
import { cancellablePromise, CancellablePromise } from './cancellablePromise';
import * as Styled from './styled';

export interface PaginatedFetchProps {
	children: Function;
	tableHeader: any;
	noSearchResults: JSX.Element;
	fetchNextPage: Function;
	updateTotalResultsCount: Function;
	filters: any;
	disclaimer?: any;
}

export function reducer(state: any, action: any) {
	const { payload } = action;
	let nextState;
	let nextDataSet = [];
	switch (action.type) {
		case 'LOADING':
			return {
				...state,
				loading: true,
				error: false,
			};
		case 'LOAD_NEXT_PAGE_SUCCESS':
			if (state.data && state.data.length > 0) {
				nextDataSet = [...state.data, ...payload.items];
			} else {
				nextDataSet = [...payload.items];
			}

			nextState = {
				...state,
				currentPage: payload.currentPage,
				error: false,
				data: nextDataSet,
				totalCount: payload.totalCount,
				loading: false,
			};
			return nextState;
		case 'LOAD_NEXT_PAGE_FAIL':
			nextState = {
				...state,
				error: true,
			};
			return nextState;
		case 'RESET_FETCH':
			nextState = {
				...payload,
			};
			return nextState;
		default:
			throw new Error();
	}
}

const PaginatedFetch: React.FunctionComponent<PaginatedFetchProps> = ({
	children,
	tableHeader,
	noSearchResults,
	fetchNextPage,
	updateTotalResultsCount,
	filters,
	disclaimer,
}: PaginatedFetchProps) => {
	const initialState = {
		currentPage: 0,
		loading: false,
		error: false,
		totalCount: 0,
		data: null,
	};

	//Allows us to snap the navigate to top button to the correct position after load more call has been complete
	const navToTopRef = React.useRef<NavigateToTopButton>(null);
	function updateNavToTopPosition() {
		if (navToTopRef.current) {
			navToTopRef.current.handleState();
		}
	}
	const [activeRequest, setActiveRequest] = React.useState();

	function updateResultsFocus() {
		let results: any = document.querySelectorAll(
			`[data-id='${searchTestIds.clientName}']`
		);
		if (results && results.length > state.currentPage * 25) {
			results[state.currentPage * 25].focus();
		}
	}

	const [state, dispatch] = React.useReducer(reducer, initialState);
	React.useEffect(() => {
		dispatch({
			type: 'RESET_FETCH',
			payload: { ...initialState, loading: true },
		});
		loadNextPageData(1);
	}, [JSON.stringify(filters)]);

	async function loadNextPageData(pageNumber?: number) {
		const pageNo = pageNumber ? pageNumber : state.currentPage + 1;
		const filtersForSearch = { ...filters, pageNumber: pageNo };

		if (activeRequest) {
			activeRequest.cancel();
			setActiveRequest(undefined);
		}

		const currentRequest: CancellablePromise = cancellablePromise(
			fetchNextPage,
			filtersForSearch
		);
		setActiveRequest(currentRequest);

		currentRequest.promise
			.then((data: any) => {
				dispatch({
					type: 'LOAD_NEXT_PAGE_SUCCESS',
					payload: {
						items: data.items,
						totalCount: data.totalCount,
						currentPage: data.page,
					},
				});
				updateTotalResultsCount(data.totalCount);
				setActiveRequest(undefined);
				updateResultsFocus();
				updateNavToTopPosition();
			})
			.catch(error => {
				if (error.cancelled) {
					return;
				}

				setActiveRequest(undefined);
				setTimeout(() => {
					dispatch({ type: 'LOAD_NEXT_PAGE_FAIL' });
				}, 750);
			});
	}

	async function handleLoadMoreClick() {
		dispatch({ type: 'LOADING' });
		await loadNextPageData();
	}

	function renderLoadButtons() {
		return (
			<TertiaryButton
				colorTheme="mlcOrange"
				data-id={searchTestIds.loadMoreResultsLink}
				onClick={handleLoadMoreClick}
				disabled={state.loading}
			>
				{state.loading ? (
					<React.Fragment>
						<LoadingIndicator colorTheme="dark" margin={0} />
						Loading more results...
					</React.Fragment>
				) : (
					<React.Fragment>
						Load more
						<Svg symbol={plus} />
					</React.Fragment>
				)}
			</TertiaryButton>
		);
	}

	function renderLoadingFailedMessage() {
		return (
			<Card display="flex" flexDirection="row" alignItems="center" padding={2}>
				<Svg symbol={exclamationTriangle} color={palette.mlcRed} />
				<Paragraph
					margin={0}
					padding={0}
					paddingRight={1}
					paddingLeft={1}
					color={palette.mlcRed}
				>
					Can&apos;t load more results.
				</Paragraph>
				<TextLink
					el="button"
					paddingLeft={0}
					data-id={searchTestIds.searchLoadMoreTryAgain}
					onClick={handleLoadMoreClick}
				>
					Please try again
				</TextLink>
			</Card>
		);
	}

	if (state.error && state.currentPage === 0) {
		throw new Error();
	}

	if (state.loading && !state.data) {
		return (
			<Card display="flex" justifyContent="center" alignItems="center">
				<div style={{ width: '100px' }}>
					<LoadingIndicator variant="scaled" colorTheme="dark" />
				</div>
			</Card>
		);
	}

	return (
		<React.Fragment>
			{state.data === null || state.data.length === 0 ? (
				noSearchResults
			) : (
				<Table data-table="table">
					<TableHead>{tableHeader}</TableHead>
					<TableBody data-id={searchTestIds.tableBodyTest}>
						{children(state.data)}
					</TableBody>
				</Table>
			)}
			{disclaimer ? <Card marginTop={4}>{disclaimer}</Card> : null}

			<Styled.LoadMoreContainer>
				{state.data && state.data.length < state.totalCount
					? state.error
						? renderLoadingFailedMessage()
						: renderLoadButtons()
					: null}
			</Styled.LoadMoreContainer>
			{state.currentPage > 1 ? (
				<NavigateToTopButton
					footerQuerySelector='[data-analytics-group="tools_quickLinks"]'
					ref={navToTopRef}
				/>
			) : null}
		</React.Fragment>
	);
};

export default PaginatedFetch;
